/**
 * Display modes.
 *
 * Each mode maps the simulation state to a colour. The list is the contract
 * between the simulation (which owns the fields) and the renderer (which owns the
 * palettes), so adding a field means adding a mode here plus a ramp below.
 *
 * The `id` is the simulation's vocabulary and stays English; the labels and hints
 * are localised and can be rewritten freely.
 */
export type ViewMode =
  | 'terrain'
  | 'height'
  | 'slope'
  | 'water'
  | 'sediment'
  | 'velocity'
  | 'erosion'
  | 'deposition'
  | 'hardness'
  | 'soil';

export interface ViewModeInfo {
  id: ViewMode;
  label: string;
  /** Shown in the Info panel so a mode is self-explanatory. */
  hint: string;
}

export const VIEW_MODES: ViewModeInfo[] = [
  { id: 'terrain', label: '地形', hint: '带光照的地貌，含水体和风化层着色' },
  { id: 'height', label: '高程', hint: '由基准面到峰顶的高程色带' },
  { id: 'slope', label: '坡度', hint: '地表梯度大小；越陡越深' },
  { id: 'water', label: '水深', hint: '每个网格上的水深' },
  { id: 'sediment', label: '泥沙', hint: '水流携带的悬移质浓度' },
  { id: 'velocity', label: '流速', hint: '垂线平均流速' },
  { id: 'erosion', label: '侵蚀', hint: '本 tick 被剥蚀掉的物质' },
  { id: 'deposition', label: '沉积', hint: '本 tick 堆积下来的物质' },
  { id: 'hardness', label: '硬度', hint: '基岩抗侵蚀能力' },
  { id: 'soil', label: '风化层', hint: '基岩之上、可被搬运的风化层厚度' },
];

export type RGB = [number, number, number];

/**
 * Scientific-visualisation ramps.
 *
 * Hand-picked stops instead of a generic rainbow: a rainbow ramp invents
 * structure that is not in the data (the eye reads the yellow-green boundary as
 * a contour), whereas these stay monotone in lightness so magnitude is legible.
 */
type Stop = { t: number; c: RGB };

/** Colour ramp with an allocation-free form for the per-pixel renderer. */
export interface ColorRamp {
  (t: number): RGB;
  into(t: number, out: RGB): RGB;
}

function ramp(stops: Stop[]): ColorRamp {
  const into = (t: number, out: RGB): RGB => {
    const x = t < 0 ? 0 : t > 1 ? 1 : t;
    for (let i = 1; i < stops.length; i++) {
      if (x <= stops[i].t) {
        const a = stops[i - 1];
        const b = stops[i];
        const k = (x - a.t) / Math.max(1e-9, b.t - a.t);
        out[0] = a.c[0] + (b.c[0] - a.c[0]) * k;
        out[1] = a.c[1] + (b.c[1] - a.c[1]) * k;
        out[2] = a.c[2] + (b.c[2] - a.c[2]) * k;
        return out;
      }
    }
    const last = stops[stops.length - 1].c;
    out[0] = last[0];
    out[1] = last[1];
    out[2] = last[2];
    return out;
  };
  const fn = ((t: number): RGB => into(t, [0, 0, 0])) as ColorRamp;
  fn.into = into;
  return fn;
}

/** Elevation: pale sand through olive to grey rock and snow. */
export const RAMP_HEIGHT = ramp([
  { t: 0.0, c: [214, 205, 178] },
  { t: 0.25, c: [178, 176, 132] },
  { t: 0.5, c: [136, 140, 104] },
  { t: 0.72, c: [120, 108, 96] },
  { t: 0.9, c: [156, 152, 150] },
  { t: 1.0, c: [242, 242, 240] },
]);

/** Magnitude (slope, velocity, concentration): light to dark, single hue. */
export const RAMP_MAGNITUDE = ramp([
  { t: 0.0, c: [247, 245, 238] },
  { t: 0.35, c: [196, 178, 140] },
  { t: 0.7, c: [150, 106, 72] },
  { t: 1.0, c: [72, 44, 34] },
]);

/** Water: shallow to deep. */
export const RAMP_WATER = ramp([
  { t: 0.0, c: [226, 236, 238] },
  { t: 0.3, c: [150, 190, 206] },
  { t: 0.7, c: [74, 124, 155] },
  { t: 1.0, c: [32, 62, 96] },
]);

/** Signed change: deposition (warm) through neutral to erosion (cold). */
export const RAMP_EROSION = ramp([
  { t: 0.0, c: [42, 78, 118] },
  { t: 0.42, c: [214, 226, 232] },
  { t: 0.5, c: [242, 240, 234] },
  { t: 0.58, c: [232, 208, 176] },
  { t: 1.0, c: [146, 86, 46] },
]);

/** Hardness: soft to hard rock. */
export const RAMP_HARDNESS = ramp([
  { t: 0.0, c: [238, 228, 206] },
  { t: 0.5, c: [186, 158, 120] },
  { t: 1.0, c: [88, 76, 74] },
]);

export function rampFor(mode: ViewMode): ColorRamp {
  switch (mode) {
    case 'terrain':
    case 'height':
      return RAMP_HEIGHT;
    case 'water':
      return RAMP_WATER;
    case 'erosion':
    case 'deposition':
      return RAMP_EROSION;
    case 'hardness':
      return RAMP_HARDNESS;
    default:
      return RAMP_MAGNITUDE;
  }
}
